import React, { useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'

import styled from 'styled-components'
import { theme } from 'styled-tools'

import Loader from '../../components/loader/Loader'
import Header from '../../components/header'

import AmendStep1 from './AmendStep1'
import AmendStep2 from './AmendStep2'
import AmendStep3 from './AmendStep3'

import useAppointment from '../../hooks/appointment/useAppointment'
import useTranslation from '../../../utils/translation'
import useServiceProviderById from '../../hooks/service-provider/useServiceProviderById'

import { isEmbedded } from '../bada/utils'
import {
  checkMeetingHasPassed,
} from '../appointment-details/utils'
import { useCms } from '../../components/cms/CmsContext'
import { useGetStore } from '../../../utils/badaAppointmentApi'
import { SIMPLY_BOOK_ME } from '../bada/reducer/Wizard'

const MainContainer = styled.div`
  padding: 20px;
  max-width: 1097px;
  margin: 0px auto 400px auto;
`

const LoaderCover = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
`

const LoaderContainer = styled.div`
  margin-top: 40vh;
`

export const ErrorMessage = styled.h1`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-family: ${theme('typography.amend.headline.fontFamily')};
  font-weight: ${theme('amend.error.fontWeight')};
  font-size: 36px;
  text-align: center;
  margin-top: 40px;
`

const validateAppointment = (appointment, step) => {
  const failedFetchingAppointment = !appointment
  if (failedFetchingAppointment) return 'appointment-not-found'

  const appointmentHasPassed = checkMeetingHasPassed(
    appointment.selectedDate,
    appointment.selectedTime
  )
  if (appointmentHasPassed) return 'appointment-passed'

  const appointmentTypeCanBeCanceled = appointment.selectedTime !== null
  if (!appointmentTypeCanBeCanceled) return 'not-slot-booking'

  const appointmentCanceled =
    appointment.simplyBookMeStatus === 'canceled' && step !== '3'
  if (appointmentCanceled) return 'appointment-canceled'

  return null
}

const AmendPage = () => {
  const location = useLocation()
  const t = useTranslation('amend')
  const { appointmentId } = useParams()
  const {
    data: appointment,
    isLoading: appointmentIsLoading,
    refetch: refetchAppointment,
  } = useAppointment(appointmentId)
  const { data: store, isLoading: storeIsLoading } = useGetStore(
    appointment?.storeId
  )
  const { services } = useCms()

  const { data: selectedServiceProvider, isLoading: loadingServiceProvider } =
    useServiceProviderById(
      appointment?.storeId,
      appointment?.simplyBookMeSubsystem,
      appointment?.serviceProviderId
    )

  const mappedStore = store
    ? {
        ...store,
        id: store.id,
        simplyBookMeSubsystem:
          store.externalMeetingData?.meetingData?.simplyBookMeSubsystem,
        services:
          store.serviceSettings?.map((s) => ({
            id: s.serviceId,
            disabled: s.disabled,
            adminEnabled: s.adminEnabled,
          })) || [],
        bookingSystem: SIMPLY_BOOK_ME,
        hasSimplyBookMe:
          store.externalMeetingData?.meetingData?.hasSimplyBookMe,
        acceptHomeVisits:
          store.externalMeetingData?.meetingData?.acceptHomeVisits,
      }
    : {}

  const loading = useMemo(
    () => appointmentIsLoading || storeIsLoading,
    [appointmentIsLoading, storeIsLoading]
  )

  const step = useMemo(
    () => new URLSearchParams(location.search).get('step'),
    [location.search]
  )

  const errorMessage = useMemo(
    () => validateAppointment(appointment, step),
    [appointment, step]
  )

  if (loading || loadingServiceProvider) {
    return (
      <LoaderCover>
        <LoaderContainer>
          <Loader duration={1} />
        </LoaderContainer>
      </LoaderCover>
    )
  }

  return (
    <>
      {!isEmbedded() && <Header />}
      <MainContainer>
        {errorMessage ? (
          <ErrorMessage>{t(errorMessage)}</ErrorMessage>
        ) : (
          (() => {
            switch (step) {
              case 1:
              default:
                return (
                  <AmendStep1
                    appointment={appointment}
                    store={mappedStore}
                    services={services}
                    selectedServiceProvider={selectedServiceProvider}
                  />
                )
              case '2':
                return (
                  <AmendStep2
                    appointment={appointment}
                    store={mappedStore}
                    pageContentServices={services}
                    refetchAppointment={refetchAppointment}
                    selectedServiceProvider={selectedServiceProvider}
                  />
                )
              case '3':
                return (
                  <AmendStep3
                    appointment={appointment}
                    store={store}
                    pageContentServices={services}
                  />
                )
            }
          })()
        )}
      </MainContainer>
    </>
  )
}

export default AmendPage
